import {
  createChecklistFailed,
  createChecklistSucceeded,
  deleteChecklistFailed,
  deleteChecklistSucceeded,
  loadChecklistFailed,
  loadChecklistsFailed,
  loadChecklistsSucceeded,
  loadChecklistSucceeded,
  loadCompletedChecklistsNamesFailed,
  loadCompletedChecklistsNamesSucceeded,
  updateChecklistFailed,
  updateChecklistSucceeded,
} from './actionCreators';
import type {
  CreateChecklistRequest,
  DeleteChecklistRequest,
  LoadChecklistRequest,
  LoadCompletedChecklistsNamesRequest,
  LoadCompletedChecklistsRequest,
  UpdateChecklistRequest,
} from './actions';
import { ChecklistActionTypes } from './actionTypes';
import { all, call, put, takeLatest } from 'redux-saga/effects';

import type { UUID } from '@cobliteam/common-types';

import { type Checklist, type ChecklistsNames, type ChecklistsPage, GraphQLChecklistsRequester } from '@cobliteam/api-lib';

import Alert from '@cobliteam/ui/components/Alert';

export const graphQLChecklistsRequester: GraphQLChecklistsRequester = new GraphQLChecklistsRequester();

export function* requestLoadTemplateChecklistsSaga() {
  try {
    const checklistsResponse: ChecklistsPage = yield call(graphQLChecklistsRequester.getTemplateChecklists);

    yield put(loadChecklistsSucceeded(checklistsResponse, true));
  } catch (err) {
    yield put(loadChecklistsFailed(true));
    Alert.error('Houve um erro, tente novamente mais tarde.');
  }
}

export function* requestLoadCompletedChecklistsSaga(action: LoadCompletedChecklistsRequest) {
  const { period, page, size, sort, inputFilter, nameFilters } = action.payload;

  try {
    const checklistsResponse: ChecklistsPage = yield call(graphQLChecklistsRequester.getCompletedChecklists, period, page, size, sort, inputFilter, nameFilters);

    yield put(loadChecklistsSucceeded(checklistsResponse, false));
  } catch (err) {
    yield put(loadChecklistsFailed(false));
    Alert.error('Houve um erro, tente novamente mais tarde.');
  }
}

export function* requestLoadChecklistSaga(action: LoadChecklistRequest) {
  const checklistId = action.payload;

  try {
    const checklistResponse: Checklist = yield call(graphQLChecklistsRequester.getChecklistById, checklistId);

    yield put(loadChecklistSucceeded(checklistResponse));
  } catch (err) {
    yield put(loadChecklistFailed());
    Alert.error('Houve um erro, tente novamente mais tarde.');
  }
}

export function* requestLoadCompletedChecklistsNames(action: LoadCompletedChecklistsNamesRequest) {
  const template = action.payload;

  try {
    const checklistsNames: ChecklistsNames = yield call(graphQLChecklistsRequester.getChecklistsNames, template);

    yield put(loadCompletedChecklistsNamesSucceeded(checklistsNames));
  } catch (err) {
    yield put(loadCompletedChecklistsNamesFailed());
    Alert.error('Houve um erro, tente novamente mais tarde.');
  }
}

export function* requestCreateChecklistSaga(action: CreateChecklistRequest) {
  const createdChecklist = action.payload;

  try {
    const createdChecklistResponse: Checklist = yield call(graphQLChecklistsRequester.createChecklist, createdChecklist);

    yield put(createChecklistSucceeded(createdChecklistResponse));
    Alert.success('Checklist criada com sucesso!');
  } catch (err) {
    yield put(createChecklistFailed());
    Alert.error('Houve um erro, tente novamente mais tarde.');
  }
}

export function* requestUpdateChecklistSaga(action: UpdateChecklistRequest) {
  const { id, updatedChecklist } = action.payload;

  try {
    const updatedChecklistResponse: Checklist = yield call(graphQLChecklistsRequester.updateChecklist, id, updatedChecklist);
    yield put(updateChecklistSucceeded(updatedChecklistResponse));
    Alert.success('Checklist atualizada com sucesso!');
  } catch (err) {
    yield put(updateChecklistFailed());

    // @ts-ignore we must validate error type to access its data
    const errorMessage = err.message.includes('404: Not Found')
      ? 'Checklist não encontrada, verifique se ela já foi excluída ou atualize a página e tente novamente'
      : 'Houve um erro, tente novamente mais tarde.';
    Alert.error(errorMessage);
  }
}

export function* requestDeleteChecklistSaga(action: DeleteChecklistRequest) {
  const deletedChecklist = action.payload;

  try {
    const deletedChecklistIdResponse: UUID = yield call(graphQLChecklistsRequester.deleteChecklist, deletedChecklist.id);

    yield put(deleteChecklistSucceeded(deletedChecklistIdResponse, deletedChecklist.template));
    Alert.success('Checklist removida com sucesso!');
  } catch (err) {
    yield put(deleteChecklistFailed());
    Alert.error('Houve um erro, tente novamente mais tarde.');
  }
}

export function* ChecklistSagasListener() {
  yield all([
    takeLatest(ChecklistActionTypes.LOAD_TEMPLATE_CHECKLISTS_REQUEST, requestLoadTemplateChecklistsSaga),
    takeLatest(ChecklistActionTypes.LOAD_COMPLETED_CHECKLISTS_REQUEST, requestLoadCompletedChecklistsSaga),
    takeLatest(ChecklistActionTypes.LOAD_COMPLETED_CHECKLISTS_NAMES_REQUEST, requestLoadCompletedChecklistsNames),
    takeLatest(ChecklistActionTypes.LOAD_CHECKLIST_REQUEST, requestLoadChecklistSaga),
    takeLatest(ChecklistActionTypes.CREATE_CHECKLIST_REQUEST, requestCreateChecklistSaga),
    takeLatest(ChecklistActionTypes.UPDATE_CHECKLIST_REQUEST, requestUpdateChecklistSaga),
    takeLatest(ChecklistActionTypes.DELETE_CHECKLIST_REQUEST, requestDeleteChecklistSaga),
  ]);
}
